import React, { useState, useEffect } from 'react';
import { apiBaseUrl } from '../config.js';

let ioInstance = null;

const Metrics = ({ userToken }) => {
    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };
    
    const getTomorrowDate = () => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const year = tomorrow.getFullYear();
        const month = String(tomorrow.getMonth() + 1).padStart(2, '0');
        const day = String(tomorrow.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const [platformMetrics, setPlatformMetrics] = useState(null);
    const [tasaciones, setTasaciones] = useState([]);
    const [tasacionesPagination, setTasacionesPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeUsers, setActiveUsers] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('platform');
    // Default startDate to today, endDate to tomorrow (+1 day)
    const [startDate, setStartDate] = useState(getTodayDate());
    const [endDate, setEndDate] = useState(getTomorrowDate());
    const [realtimeNotification, setRealtimeNotification] = useState(null);
    const [leadSources, setLeadSources] = useState(null);
    const [appointments, setAppointments] = useState([]);
    const [appointmentsLoading, setAppointmentsLoading] = useState(false);

    useEffect(() => {
        if (userToken) {
            fetchMetrics(1);
            setupSocket();
            const poll = setInterval(fetchRealtimeFallback, 15000);
            return () => {
                clearInterval(poll);
                if (ioInstance) {
                    ioInstance.off('active_users');
                    ioInstance.off('new_tasacion');
                }
            };
        }
    }, [userToken]);
    
    // Note: We removed the auto-refetch on date change to avoid duplicate fetches
    // Users need to click "Filtrar" button to apply date changes

    const setupSocket = async () => {
        try {
            if (!ioInstance) {
                const { io } = await import('socket.io-client');
                ioInstance = io(apiBaseUrl, { transports: ['websocket'], withCredentials: false });
            }
            
            ioInstance.on('active_users', (count) => {
                setActiveUsers(count);
            });
            
            ioInstance.on('new_tasacion', (data) => {
                setRealtimeNotification({
                    message: `Nueva tasación: ${data.zone || 'N/A'} - ${data.type || 'N/A'}`,
                    timestamp: data.timestamp
                });
                
                // Auto-refresh metrics (reset to page 1)
                fetchMetrics(1);
                
                // Clear notification after 5 seconds
                setTimeout(() => setRealtimeNotification(null), 5000);
            });
        } catch (e) {
        }
    };

    const fetchRealtimeFallback = async () => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/stats/realtime`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (typeof data.activeUsers === 'number') setActiveUsers(data.activeUsers);
            }
        } catch (_) {}
    };

    const fetchAppointments = async () => {
        setAppointmentsLoading(true);
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };
            const res = await fetch(`${apiBaseUrl}/api/admin/martillero-appointments?limit=100`, { headers });
            if (res.ok) {
                const data = await res.json();
                if (data.success) {
                    setAppointments(data.appointments);
                }
            }
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setAppointmentsLoading(false);
        }
    };

    // Fetch appointments when tab changes to appointments
    useEffect(() => {
        if (activeTab === 'appointments' && userToken) {
            fetchAppointments();
        }
    }, [activeTab, userToken]);

    const updateAppointmentStatus = async (appointmentId, newStatus) => {
        try {
            const headers = {
                'Authorization': `Bearer ${userToken}`,
                'Content-Type': 'application/json'
            };
            await fetch(`${apiBaseUrl}/api/admin/martillero-appointments/${appointmentId}`, {
                method: 'PUT',
                headers,
                body: JSON.stringify({ status: newStatus })
            });
            
            // Update local state
            setAppointments(prev => prev.map(apt => 
                apt._id === appointmentId ? { ...apt, status: newStatus } : apt
            ));
        } catch (err) {
            console.error('Error updating appointment status:', err);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Fetch only tasaciones table (for pagination)
    const fetchTasaciones = async (page = 1) => {
        try {
            // Build query parameters for date range
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', page);
            params.append('limit', '20');
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Headers with authentication token
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const tasacionesResponse = await fetch(`${apiBaseUrl}/api/metrics/tasaciones${queryString}`, { headers });
            
            if (tasacionesResponse.ok) {
                const data = await tasacionesResponse.json();
                setTasaciones(data.tasaciones || []);
                setTasacionesPagination(data.pagination || null);
                setCurrentPage(page);
            }
        } catch (err) {
            console.error('Error fetching tasaciones:', err);
        }
    };

    const fetchMetrics = async (page = 1) => {
        try {
            setLoading(true);
            setError(null);

            // Build query parameters for date range
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            params.append('page', page);
            params.append('limit', '20');
            const queryString = params.toString() ? `?${params.toString()}` : '';

            // Headers with authentication token
            const headers = {
                'Authorization': `Bearer ${userToken}`
            };

            const [platformResponse, tasacionesResponse, leadSourcesResponse] = await Promise.all([
                fetch(`${apiBaseUrl}/api/metrics/platform${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/tasaciones${queryString}`, { headers }),
                fetch(`${apiBaseUrl}/api/metrics/lead-sources${queryString}`, { headers })
            ]);

            // Check for authorization errors
            if (platformResponse.status === 403 || platformResponse.status === 401) {
                setError('Acceso denegado. Se requieren privilegios de administrador.');
                setLoading(false);
                return;
            }

            if (platformResponse.ok) setPlatformMetrics(await platformResponse.json());
            
            if (tasacionesResponse.ok) {
                const data = await tasacionesResponse.json();
                setTasaciones(data.tasaciones || []);
                setTasacionesPagination(data.pagination || null);
                setCurrentPage(page);
            }
            
            if (leadSourcesResponse.ok) {
                const data = await leadSourcesResponse.json();
                setLeadSources(data);
            }
            
            await fetchRealtimeFallback();
        } catch (err) {
            setError('Error al cargar las métricas');
        } finally {
            setLoading(false);
        }
    };

    const formatNumber = (num) => num?.toLocaleString?.() ?? num;
    const formatCurrency = (num) => `$${Number(num || 0).toLocaleString()}`;

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: '#666' }}>
                📊 Cargando métricas...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', fontSize: '18px', color: '#e74c3c' }}>
                ❌ {error}
            </div>
        );
    }

    return (
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', fontFamily: 'Arial, sans-serif' }}>
            <h1 style={{ textAlign: 'center', color: '#2c3e50', marginBottom: '20px', fontSize: '28px' }}>
                📊 Dashboard de Métricas
            </h1>

            {/* Real-time notification */}
            {realtimeNotification && (
                <div style={{ 
                    backgroundColor: '#27ae60', 
                    color: 'white', 
                    padding: '12px 20px', 
                    borderRadius: '8px', 
                    marginBottom: '20px', 
                    textAlign: 'center',
                    fontWeight: 'bold',
                    animation: 'fadeIn 0.3s ease-in'
                }}>
                    🔔 {realtimeNotification.message}
                </div>
            )}

            {/* Date Range Filter */}
            <div style={{ 
                backgroundColor: 'white', 
                padding: '20px', 
                borderRadius: '12px', 
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
                marginBottom: '20px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '15px',
                alignItems: 'center',
                justifyContent: 'center'
            }}>
                <div style={{ width: '100%', textAlign: 'center', marginBottom: '10px', color: '#7f8c8d', fontSize: '13px' }}>
                    {!startDate && !endDate ? '📊 Mostrando todos los datos' : `📅 Filtrando desde: ${startDate || 'inicio'}${endDate ? ` hasta: ${endDate}` : ' (hasta ahora)'}`}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontWeight: 'bold', color: '#2c3e50' }}>Desde:</label>
                    <input 
                        type="date" 
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{ 
                            padding: '8px 12px', 
                            border: '2px solid #e1e8ed', 
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <label style={{ fontWeight: 'bold', color: '#2c3e50' }}>Hasta:</label>
                    <input 
                        type="date" 
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{ 
                            padding: '8px 12px', 
                            border: '2px solid #e1e8ed', 
                            borderRadius: '6px',
                            fontSize: '14px'
                        }}
                    />
                </div>
                <button 
                    onClick={() => fetchMetrics(1)}
                    style={{ 
                        padding: '8px 20px', 
                        backgroundColor: '#3498db', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px',
                        transition: 'background-color 0.2s'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}
                >
                    🔍 Filtrar
                </button>
                <button 
                    onClick={() => {
                        const today = getTodayDate();
                        setStartDate(today);
                        setEndDate(today);
                        setTimeout(fetchMetrics, 100);
                    }}
                    style={{ 
                        padding: '8px 20px', 
                        backgroundColor: '#27ae60', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    📅 Hoy
                </button>
                <button 
                    onClick={() => {
                        setStartDate('');
                        setEndDate('');
                        setTimeout(fetchMetrics, 100);
                    }}
                    style={{ 
                        padding: '8px 20px', 
                        backgroundColor: '#95a5a6', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '14px'
                    }}
                >
                    ∞ Todo
                </button>
            </div>

            {/* Live tiles row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                    <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Usuarios activos ahora</div>
                    <div style={{ fontSize: '34px', fontWeight: '800', color: '#8e44ad' }}>{activeUsers ?? '—'}</div>
                    <div style={{ fontSize: '12px', color: '#95a5a6', marginTop: '6px' }}>Tiempo real (Socket.IO)</div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px', gap: '10px', flexWrap: 'wrap' }}>
                <button onClick={() => setActiveTab('platform')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'platform' ? '#3498db' : '#ecf0f1', color: activeTab === 'platform' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>📈 Plataforma</button>
                <button onClick={() => setActiveTab('tasaciones')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'tasaciones' ? '#3498db' : '#ecf0f1', color: activeTab === 'tasaciones' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>🏠 Tasaciones</button>
                <button onClick={() => setActiveTab('leadSources')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'leadSources' ? '#3498db' : '#ecf0f1', color: activeTab === 'leadSources' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>🎯 Fuentes de Leads</button>
                <button onClick={() => setActiveTab('appointments')} style={{ padding: '12px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', backgroundColor: activeTab === 'appointments' ? '#3498db' : '#ecf0f1', color: activeTab === 'appointments' ? 'white' : '#2c3e50', transition: 'all 0.3s ease' }}>
                    📅 Citas Martillero
                    {appointments.filter(apt => apt.status === 'pending').length > 0 && activeTab !== 'appointments' && (
                        <span style={{ marginLeft: '8px', backgroundColor: '#e74c3c', color: 'white', padding: '2px 8px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
                            {appointments.filter(apt => apt.status === 'pending').length}
                        </span>
                    )}
                </button>
            </div>

            {/* Platform Metrics Tab */}
            {activeTab === 'platform' && platformMetrics && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>🏠 Tasaciones Totales</h3>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{formatNumber(platformMetrics.totalValuations)}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Últimos 30 días: {formatNumber(platformMetrics.recentValuations)}</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👤 Usuarios Únicos</h3>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9b59b6' }}>{formatNumber(platformMetrics.uniqueUsersCount)}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Teléfonos únicos con tasaciones</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👥 Usuarios Registrados</h3>
                        <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{formatNumber(platformMetrics.totalUsers)}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Nuevos (30 días): {formatNumber(platformMetrics.newUsers)}</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>🏙️ Distribución por Zona</h3>
                        <div style={{ marginBottom: '10px' }}><span style={{ color: '#3498db' }}>CABA:</span> {formatNumber(platformMetrics.cabaValuations)}</div>
                        <div><span style={{ color: '#e67e22' }}>Provincia:</span> {formatNumber(platformMetrics.provinciaValuations)}</div>
                    </div>
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>💰 Precio Promedio</h3>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f39c12' }}>{platformMetrics.avgPriceRange}</div>
                        <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Rango promedio por tasación</div>
                    </div>
                </div>
            )}

            {/* Tasaciones Tab */}
            {activeTab === 'tasaciones' && (
                <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                    <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>🏠 Últimas Tasaciones</h3>
                    {tasaciones.length > 0 ? (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Fecha</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Dirección</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Operación</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Rango de Precio</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Teléfono</th>
                                            <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                                            <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>IP</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {tasaciones.map((tasacion, index) => (
                                            <tr key={index} style={{ borderBottom: '1px solid #dee2e6' }}>
                                                <td style={{ padding: '12px', fontSize: '12px', color: '#7f8c8d' }}>
                                                    {tasacion.timestamp ? new Date(tasacion.timestamp).toLocaleDateString('es-ES', { 
                                                        year: 'numeric',
                                                        month: 'short', 
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    }) : 'N/A'}
                                                </td>
                                                <td style={{ padding: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tasacion.address}>
                                                    {tasacion.address}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    <span style={{
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '12px',
                                                        fontWeight: 'bold',
                                                        backgroundColor: tasacion.operation === 'alquiler' ? '#e3f2fd' : '#f3e5f5',
                                                        color: tasacion.operation === 'alquiler' ? '#1976d2' : '#7b1fa2'
                                                    }}>
                                                        {tasacion.operation === 'alquiler' ? '🏠 Alquiler' : '💰 Venta'}
                                                    </span>
                                                </td>
                                                <td style={{ 
                                                    padding: '12px', 
                                                    textAlign: 'center', 
                                                    color: '#27ae60', 
                                                    fontWeight: 'bold', 
                                                    fontSize: tasacion.operation === 'alquiler' ? '12px' : '13px',
                                                    minWidth: '200px',
                                                    lineHeight: '1.4'
                                                }}>
                                                    {tasacion.priceRange}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center' }}>
                                                    {tasacion.phone}
                                                </td>
                                                <td style={{ padding: '12px', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={tasacion.email}>
                                                    {tasacion.email}
                                                </td>
                                                <td style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: '#7f8c8d', fontFamily: 'monospace' }}>
                                                    {tasacion.ip || 'N/A'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* Pagination Controls */}
                            {tasacionesPagination && tasacionesPagination.totalPages > 1 && (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '20px', gap: '10px' }}>
                                    <button
                                        onClick={() => fetchTasaciones(currentPage - 1)}
                                        disabled={!tasacionesPagination.hasPrevPage}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: tasacionesPagination.hasPrevPage ? '#3498db' : '#ecf0f1',
                                            color: tasacionesPagination.hasPrevPage ? 'white' : '#95a5a6',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: tasacionesPagination.hasPrevPage ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        ← Anterior
                                    </button>
                                    
                                    <span style={{ color: '#2c3e50', fontSize: '14px', fontWeight: 'bold' }}>
                                        Página {tasacionesPagination.currentPage} de {tasacionesPagination.totalPages}
                                        <span style={{ color: '#7f8c8d', fontSize: '12px', marginLeft: '8px' }}>
                                            ({tasacionesPagination.totalCount} total)
                                        </span>
                                    </span>
                                    
                                    <button
                                        onClick={() => fetchTasaciones(currentPage + 1)}
                                        disabled={!tasacionesPagination.hasNextPage}
                                        style={{
                                            padding: '8px 16px',
                                            backgroundColor: tasacionesPagination.hasNextPage ? '#3498db' : '#ecf0f1',
                                            color: tasacionesPagination.hasNextPage ? 'white' : '#95a5a6',
                                            border: 'none',
                                            borderRadius: '6px',
                                            cursor: tasacionesPagination.hasNextPage ? 'pointer' : 'not-allowed',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        Siguiente →
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                            No hay tasaciones disponibles
                        </div>
                    )}
                </div>
            )}

            {/* Lead Sources Tab */}
            {activeTab === 'leadSources' && leadSources && (
                <div>
                    {/* Summary Cards */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👥 Visitantes Totales</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{formatNumber(leadSources.totals.totalVisitors)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Visitantes únicos</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>✅ Conversiones</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{formatNumber(leadSources.totals.totalConversions)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Tasaciones completadas</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>👤 Usuarios Únicos</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e67e22' }}>{formatNumber(leadSources.totals.totalUniqueUserConversions || 0)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Conversiones únicas</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <h3 style={{ color: '#2c3e50', marginBottom: '15px', fontSize: '18px' }}>📊 Tasa de Conversión</h3>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9b59b6' }}>{leadSources.totals.avgConversionRate}%</div>
                            <div style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '5px' }}>Promedio general</div>
                        </div>
                    </div>

                    {/* Lead Sources Table */}
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>🎯 Fuentes de Leads por Performance</h3>
                        {leadSources.leadSources && leadSources.leadSources.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Referencia</th>
                                            <th style={{ padding: '15px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Fuente</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Visitantes</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Conversiones</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Usuarios Únicos</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Tasa Conv.</th>
                                            <th style={{ padding: '15px', textAlign: 'right', fontWeight: 'bold', color: '#2c3e50' }}>Páginas/Visita</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {leadSources.leadSources.map((source, index) => {
                                            // Source name formatting with emoji
                                            const getSourceDisplay = (sourceName) => {
                                                const sourceMap = {
                                                    'google_ads': '🎯 Google Ads',
                                                    'tiktok_ads': '🎵 TikTok Ads',
                                                    'facebook_ads': '👥 Facebook Ads',
                                                    'instagram_ads': '📷 Instagram Ads',
                                                    'google_organic': '🔍 Google Orgánico',
                                                    'facebook_organic': '👥 Facebook Orgánico',
                                                    'instagram_organic': '📷 Instagram Orgánico',
                                                    'tiktok_organic': '🎵 TikTok Orgánico',
                                                    'youtube': '📺 YouTube',
                                                    'referral': '🔗 Referencia',
                                                    'organic': '🌐 Directo/Orgánico',
                                                    'unknown': '❓ Desconocido'
                                                };
                                                return sourceMap[sourceName] || `📌 ${sourceName}`;
                                            };

                                            // Get clean reference name (platform only)
                                            const getReference = (sourceName) => {
                                                const referenceMap = {
                                                    'google_ads': 'Google',
                                                    'tiktok_ads': 'TikTok',
                                                    'facebook_ads': 'Facebook',
                                                    'instagram_ads': 'Instagram',
                                                    'google_organic': 'Google',
                                                    'facebook_organic': 'Facebook',
                                                    'instagram_organic': 'Instagram',
                                                    'tiktok_organic': 'TikTok',
                                                    'youtube': 'YouTube',
                                                    'referral': 'Referencia',
                                                    'organic': 'Directo',
                                                    'unknown': 'Desconocido'
                                                };
                                                return referenceMap[sourceName] || sourceName;
                                            };

                                            const convRate = source.conversionRate || 0;
                                            const convRateColor = convRate > 10 ? '#27ae60' : convRate > 5 ? '#f39c12' : '#e74c3c';

                                            return (
                                                <tr key={index} style={{ borderBottom: '1px solid #dee2e6', transition: 'background-color 0.2s' }} onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}>
                                                    <td style={{ padding: '15px', fontWeight: '700', color: '#2c3e50', fontSize: '16px' }}>{getReference(source.source)}</td>
                                                    <td style={{ padding: '15px', fontWeight: '600', color: '#7f8c8d' }}>{getSourceDisplay(source.source)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#3498db', fontWeight: '600' }}>{formatNumber(source.visitors)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#27ae60', fontWeight: '600' }}>{formatNumber(source.conversions)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#e67e22', fontWeight: '600' }}>{formatNumber(source.uniqueUserConversions || 0)}</td>
                                                    <td style={{ padding: '15px', textAlign: 'right', fontWeight: '700', color: convRateColor }}>
                                                        {convRate}%
                                                    </td>
                                                    <td style={{ padding: '15px', textAlign: 'right', color: '#7f8c8d' }}>{source.avgPageViews}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                                No hay datos de fuentes de leads disponibles
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Appointments Tab */}
            {activeTab === 'appointments' && (
                <div>
                    {/* Appointment Stats */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '30px' }}>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Total Citas</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{appointments.length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Pendientes</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f39c12' }}>{appointments.filter(apt => apt.status === 'pending').length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Contactados</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3498db' }}>{appointments.filter(apt => apt.status === 'contacted').length}</div>
                        </div>
                        <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                            <div style={{ fontSize: '14px', color: '#7f8c8d', marginBottom: '8px' }}>Completados</div>
                            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#27ae60' }}>{appointments.filter(apt => apt.status === 'completed').length}</div>
                        </div>
                    </div>

                    {/* Appointments Table */}
                    <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e1e8ed' }}>
                        <h3 style={{ color: '#2c3e50', marginBottom: '20px', fontSize: '20px' }}>📅 Citas con Martillero</h3>
                        
                        {appointmentsLoading ? (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                                ⏳ Cargando citas...
                            </div>
                        ) : appointments.length > 0 ? (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '200px' }}>📍 Dirección</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '120px' }}>📞 Teléfono</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '150px' }}>📧 Email</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>🏠 Tipo</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>Operación</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50' }}>💰 Valor Est.</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '140px' }}>📅 Fecha</th>
                                            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 'bold', color: '#2c3e50', minWidth: '140px' }}>Estado</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {appointments.map((apt, index) => {
                                            const statusColors = {
                                                pending: '#f39c12',
                                                contacted: '#3498db',
                                                completed: '#27ae60',
                                                cancelled: '#e74c3c'
                                            };
                                            const statusLabels = {
                                                pending: 'Pendiente',
                                                contacted: 'Contactado',
                                                completed: 'Completado',
                                                cancelled: 'Cancelado'
                                            };
                                            
                                            return (
                                                <tr key={apt._id} style={{ borderBottom: '1px solid #e1e8ed', backgroundColor: index % 2 === 0 ? '#fafafa' : 'white' }}>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.address}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                                        <a 
                                                            href={`https://wa.me/${apt.phone?.replace(/\D/g, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{ color: '#25D366', fontWeight: 'bold', textDecoration: 'none' }}
                                                        >
                                                            {apt.phone}
                                                        </a>
                                                    </td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.email || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.propertyType || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>
                                                        {apt.operation === 'venta' ? 'Venta' : apt.operation === 'alquiler' ? 'Alquiler' : '-'}
                                                    </td>
                                                    <td style={{ padding: '12px', fontSize: '14px' }}>{apt.estimatedValue || '-'}</td>
                                                    <td style={{ padding: '12px', fontSize: '13px', color: '#7f8c8d' }}>{formatDate(apt.createdAt)}</td>
                                                    <td style={{ padding: '12px' }}>
                                                        <select
                                                            value={apt.status}
                                                            onChange={(e) => updateAppointmentStatus(apt._id, e.target.value)}
                                                            style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                border: '2px solid #e1e8ed',
                                                                backgroundColor: statusColors[apt.status] || '#95a5a6',
                                                                color: 'white',
                                                                fontWeight: 'bold',
                                                                fontSize: '13px',
                                                                cursor: 'pointer',
                                                                width: '130px'
                                                            }}
                                                        >
                                                            <option value="pending">Pendiente</option>
                                                            <option value="contacted">Contactado</option>
                                                            <option value="completed">Completado</option>
                                                            <option value="cancelled">Cancelado</option>
                                                        </select>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', color: '#7f8c8d', padding: '40px' }}>
                                📅 No hay citas registradas todavía
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                <button onClick={() => fetchMetrics(1)} style={{ padding: '12px 24px', backgroundColor: '#3498db', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', transition: 'background-color 0.3s ease' }} onMouseOver={(e) => e.target.style.backgroundColor = '#2980b9'} onMouseOut={(e) => e.target.style.backgroundColor = '#3498db'}>
                    🔄 Actualizar Métricas
                </button>
            </div>
        </div>
    );
};

export default Metrics; 